import React from 'react';
import { motion } from 'framer-motion';
import { Hammer, Construction, CircleDot, ArrowRight } from 'lucide-react';

export const Services = ({ onQuoteClick }) => {
  const services = [
    {
      id: 'demolition',
      icon: Hammer,
      title: 'Demolition',
      description: 'Professional controlled demolition services for residential and commercial structures. We handle selective demolition, interior strip-outs, and complete building removal with precision and safety.',
      features: ['Selective Demolition', 'Interior Strip-Outs', 'Debris Removal', 'Site Clearing'],
      image: 'https://images.unsplash.com/photo-1640631005057-de521cfcd36e?auto=format&fit=crop&w=800&q=80',
      span: 'md:col-span-1',
    },
    {
      id: 'concrete-cutting',
      icon: Construction,
      title: 'Concrete Cutting',
      description: 'Expert concrete cutting services using state-of-the-art equipment. We specialize in wall sawing, flat sawing, and core drilling for any size project. Clean, precise cuts every time.',
      features: ['Wall Sawing', 'Flat Sawing', 'Road Cutting', 'Core Drilling'],
      image: 'https://images.unsplash.com/photo-1621463677998-1a90bcbaca94?auto=format&fit=crop&w=800&q=80',
      span: 'md:col-span-2',
    },
    {
      id: 'core-drilling',
      icon: CircleDot,
      title: 'Core Drilling',
      description: 'Precision core drilling for plumbing, electrical, and HVAC installations. We drill through concrete, asphalt, and masonry with accuracy and minimal disruption.',
      features: ['Utility Penetrations', 'Sample Extraction', 'Anchor Holes', 'Wet & Dry Drilling'],
      image: 'https://images.unsplash.com/photo-1731317734838-12da6c9a6139?auto=format&fit=crop&w=800&q=80',
      span: 'md:col-span-1',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block font-heading text-sm font-semibold tracking-widest uppercase text-orange-500 mb-4">
            What We Do
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900 mb-6">
            Our Services
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Comprehensive concrete services backed by years of experience and 
            cutting-edge equipment. We tackle projects of any scale.
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.id}
                variants={itemVariants}
                data-testid={`service-card-${service.id}`}
                className={`group relative overflow-hidden bg-white border border-slate-200 hover:border-orange-300 transition-all duration-300 ${service.span}`}
              >
                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent" />
                  
                  {/* Icon Badge */}
                  <div className="absolute top-4 left-4 bg-orange-500 p-3">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <h3 className="font-heading text-2xl font-bold tracking-tight uppercase text-slate-900 mb-4">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-slate-700">
                        <div className="w-1.5 h-1.5 bg-orange-500" />
                        <span className="text-sm font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <button
                    onClick={onQuoteClick}
                    className="flex items-center gap-2 text-orange-500 font-semibold group/btn hover:gap-4 transition-all duration-300"
                  >
                    Request Quote
                    <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                  </button>
                </div>

                {/* Hover accent */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-orange-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
